import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { makeRequest } from "../../utils/request";

// Types
export type TraceSpan = {
  id: number | string;
  spanId: string;
  traceId: string;
  parentId: string | null;
  name: string;
  spanKind: string;
  startTime: string;
  endTime: string;
  durationMs: number;
  statusCode: string;
  statusMessage: string | null;
  attributes: Record<string, unknown>;
  organizationId: string | null;
  agentId: string | null;
  sessionId: string | null;
  eventName: string | null;
  turnNumber: number | null;
};

export type TraceSummary = {
  id: number;
  publicId: string;
  createdAt: string;
  updatedAt: string;
  deletedAt: string | null;
  traceId: string;
  sessionId: string;
  startTime: string;
  endTime: string;
  durationMs: number;
  spanCount: number;
  rootSpanName: string | null;
  hasError: boolean;
};

export type SessionTraceData = {
  trace: TraceSummary | null;
  spans: TraceSpan[];
};

export type SessionTraceParams = {
  agentId: string;
  sessionId: string;
};

export type SessionTraceState = {
  currentTrace: SessionTraceData | null;
  currentSessionId: string | null;
  isLoading: boolean;
  error: string | null;
};

// Async thunk for fetching session trace
export const getSessionTrace = createAsyncThunk("sessionTrace/getTrace", async (params: SessionTraceParams) => {
  const { data } = await makeRequest<{ data: SessionTraceData }>(`/v1/agents/${params.agentId}/sessions/${params.sessionId}/trace`);
  return { data, sessionId: params.sessionId };
});

const initialState: SessionTraceState = {
  currentTrace: null,
  currentSessionId: null,
  isLoading: false,
  error: null,
};

const sessionTraceSlice = createSlice({
  name: "sessionTrace",
  initialState,
  reducers: {
    clearTrace: (state) => {
      state.currentTrace = null;
      state.currentSessionId = null;
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(getSessionTrace.pending, (state) => {
      state.isLoading = true;
      state.error = null;
    });
    builder.addCase(getSessionTrace.fulfilled, (state, action) => {
      state.isLoading = false;
      state.currentTrace = action.payload.data;
      state.currentSessionId = action.payload.sessionId;
      state.error = null;
    });
    builder.addCase(getSessionTrace.rejected, (state, action) => {
      state.isLoading = false;
      state.error = action.error.message || "Failed to fetch session trace";
    });
  },
});

export const { clearTrace } = sessionTraceSlice.actions;
export const sessionTraceReducer = sessionTraceSlice.reducer;
